import { useState, useEffect, useCallback } from 'react';
import { useWebSocket } from './useWebSocket';
import {
  MinecraftServersData,
  MinecraftServerStats,
  WebSocketMessage,
} from '../types/statusMonitor';
import { STATUS_MONITOR_MINECRAFT_WS_URL } from '../config/statusMonitor';
import { starchartApiService } from '../services/starchartApi';
import type { PopularityWindow } from '../types/starchart';

export function useMinecraftServers() {
  const [servers, setServers] = useState<MinecraftServersData>({});

  const handleMessage = useCallback((msg: WebSocketMessage) => {
    if (msg.type === 'minecraft_init' && msg.minecraft) {
      setServers(msg.minecraft);
    }
    if (msg.type === 'minecraft_update' && msg.id && msg.data) {
      setServers((prev) => ({
        ...prev,
        [msg.id as string]: msg.data as MinecraftServerStats,
      }));
    }
    if (msg.type === 'minecraft_delete' && msg.id) {
      setServers((prev) => {
        const next = { ...prev };
        delete next[msg.id as string];
        return next;
      });
    }
  }, []);

  useWebSocket<WebSocketMessage>(STATUS_MONITOR_MINECRAFT_WS_URL, handleMessage);

  return servers;
}

/**
 * Given the full MinecraftServersData map and a cluster ID,
 * returns all Minecraft server instances belonging to that cluster.
 */
export function getServersByCluster(
  servers: MinecraftServersData,
  clusterId: string
): MinecraftServerStats[] {
  return Object.values(servers)
    .filter((s) => s.clusterId === clusterId)
    .sort((a, b) => a.identifier.localeCompare(b.identifier));
}

// ── Popularity API ──────────────────────────────────────────────────────────

export interface PopularityEntry {
  server_id: string;
  popularity_index: number;
}

/** Maps lowercased FQDN → { index, rank (1-based) } */
export type PopularityMap = Map<string, { index: number; rank: number }>;

const POLL_MS = 60_000;

/**
 * Current popularity ranking, keyed by lowercased FQDN.
 *
 * Backed by `starchartApiService`, so this shares one cache and one client with the StarChart
 * module rather than maintaining a second fetch loop against the same API. The upstream
 * recomputes rankings every 15 minutes, so the poll here is really just a liveness refresh.
 */
export function usePopularity(window: PopularityWindow = '28d'): PopularityMap {
  const [data, setData] = useState<PopularityMap>(new Map());

  useEffect(() => {
    let cancelled = false;

    const fetchPopularity = async () => {
      try {
        const json = await starchartApiService.getRankings(window);
        if (cancelled) return;

        const map: PopularityMap = new Map();
        json.ranked_list.forEach((entry, i) => {
          map.set(entry.server_id.toLowerCase(), {
            index: entry.popularity_index,
            rank: i + 1,
          });
        });
        setData(map);
      } catch {
        // A ranking outage should not disturb whatever is already displayed.
      }
    };

    void fetchPopularity();
    const interval = setInterval(() => void fetchPopularity(), POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [window]);

  return data;
}
